import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { auth } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, ArrowLeft, KeyRound } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al iniciar sesion');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!email) { setError('Ingresa tu correo electronico'); return; }
    if (!newPassword || newPassword.length < 6) { setError('La nueva contrasena debe tener al menos 6 caracteres'); return; }
    if (newPassword !== confirmPassword) { setError('Las contrasenas no coinciden'); return; }

    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
      const res = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, new_password: newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      setSuccess('Contrasena restablecida. Ya puedes iniciar sesion.');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => { setForgotMode(false); setSuccess(''); }, 2500);
    } catch (err) {
      setError(err.message || 'Error al restablecer contrasena');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center"
      style={{ 
        backgroundImage: 'linear-gradient(135deg, rgba(0,20,50,0.85), rgba(0,60,120,0.75)), url(https://images.pexels.com/photos/5860937/pexels-photo-5860937.jpeg)'
      }}
    >
      <Card className="w-full max-w-lg animate-fadeIn overflow-hidden shadow-2xl border-0" data-testid="login-card">
        <CardHeader className="text-center bg-white py-8 border-b">
          <img 
            src="https://customer-assets.emergentagent.com/job_teamflow-manage-1/artifacts/krciwypq_2024%20Logo%20TRANSTAR%20.jpg" 
            alt="Transtar Logo" 
            className="h-28 mx-auto"
          />
        </CardHeader>
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 py-3 px-6">
          <p className="text-white text-center text-sm font-medium tracking-wide">
            {forgotMode ? 'Restablecer Contrasena' : 'Sistema de Gestion Empresarial'}
          </p>
        </div>
        <CardContent>
          {!forgotMode ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && <div className="p-3 text-sm text-red-500 bg-red-500/10 rounded-md" data-testid="login-error">{error}</div>}
              {success && <div className="p-3 text-sm text-emerald-600 bg-emerald-500/10 rounded-md">{success}</div>}
              
              <div className="space-y-2">
                <Label htmlFor="email">Correo electronico</Label>
                <Input id="email" type="email" placeholder="tu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Contrasena</Label>
                  <button type="button" className="text-xs text-primary hover:underline" onClick={() => { setForgotMode(true); setError(''); setSuccess(''); }} data-testid="forgot-password-link">
                    Olvide mi contrasena
                  </button>
                </div>
                <Input id="password" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
              </div>
              
              <Button type="submit" className="w-full font-heading text-sm uppercase tracking-wide" disabled={loading} data-testid="login-submit-btn">
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Ingresando...</> : 'Iniciar Sesion'}
              </Button>
              
              <p className="text-center text-sm text-muted-foreground">
                No tienes cuenta?{' '}
                <Link to="/register" className="text-primary hover:underline" data-testid="register-link">Registrate aqui</Link>
              </p>
            </form>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              {error && <div className="p-3 text-sm text-red-500 bg-red-500/10 rounded-md">{error}</div>}
              {success && <div className="p-3 text-sm text-emerald-600 bg-emerald-500/10 rounded-md">{success}</div>}

              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <KeyRound className="h-4 w-4" />
                <span>Ingresa tu correo y una nueva contrasena</span>
              </div>

              <div className="space-y-2">
                <Label>Correo electronico de tu cuenta</Label>
                <Input type="email" placeholder="tu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>

              <div className="space-y-2">
                <Label>Nueva contrasena</Label>
                <Input type="password" placeholder="Minimo 6 caracteres" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
              </div>

              <div className="space-y-2">
                <Label>Confirmar nueva contrasena</Label>
                <Input type="password" placeholder="Repetir contrasena" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
              </div>

              <Button type="submit" className="w-full font-heading text-sm uppercase tracking-wide" disabled={loading}>
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Procesando...</> : 'Restablecer Contrasena'}
              </Button>

              <button type="button" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mx-auto" onClick={() => { setForgotMode(false); setError(''); setSuccess(''); }}>
                <ArrowLeft className="h-3 w-3" />Volver al inicio de sesion
              </button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
